import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Percent,
  Calendar as CalendarIcon,
  Share2,
  Headset,
  BarChart2,
  BookOpen,
  Settings,
} from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card, CardContent } from '../components/ui/Card';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { SUPPORT_EMAIL } from '@/config/constants';

export default function AppTeamPage() {
  const { user } = useAuth();
  const [isAffiliate, setIsAffiliate] = useState<boolean>(false);
  const [affiliateLoading, setAffiliateLoading] = useState<boolean>(true);

  // Check if user is an affiliate
  useEffect(() => {
    const checkAffiliateStatus = async () => {
      if (!user || !user.email) {
        setIsAffiliate(false);
        setAffiliateLoading(false);
        return;
      }

      try {
        // Query by email with exact matching for security (FIXED: using .eq() not .ilike())
        const { data, error } = await supabase
          .from('profiles')
          .select('is_affiliate')
          .eq('email', user.email) // ✅ SECURE: Exact match only
          .single();

        if (error) {
          console.error('Error checking affiliate status:', error);
          setIsAffiliate(false);
        } else {
          setIsAffiliate(!!data?.is_affiliate);
        }
      } catch (err) {
        console.error('Failed to check affiliate status:', err);
        setIsAffiliate(false);
      } finally {
        setAffiliateLoading(false);
      }
    };

    checkAffiliateStatus();
  }, [user]);

  return (
    <div className="min-h-screen bg-transparent p-4 overflow-x-hidden">
      <Card className="glass-card shadow-lg border border-gray-300/50 dark:border-gray-600/50 backdrop-blur-[100px] bg-white/30 dark:bg-gray-800/30 w-full max-w-4xl mx-auto mt-6 mb-12">
        <CardContent className="px-6 md:px-8 py-8">
          {/* Back to Home */}
          <div className="mb-6">
            <Button
              as={Link}
              to="/"
              variant="ghost"
              className="flex items-center text-gray-700 dark:text-gray-300 hover:text-gray-950 dark:hover:text-gray-100 transition-colors hover:scale-105"
            >
              <ArrowLeft size={18} className="mr-1" />
              Back to Home
            </Button>
          </div>
            {/* Hero */}
            <h1 className="text-4xl md:text-5xl font-display font-bold mb-4 text-gray-950 dark:text-gray-100 text-center">
              Become a Team <span className="gradient-text">Member</span>
            </h1>
            <p className="text-lg text-gray-700 dark:text-gray-300 mb-8 max-w-3xl mx-auto text-center">
              Do you like and actually use our journal? Nice! You can now recommend it to your community
              and earn up to <strong className="text-primary-700 dark:text-primary-400">40% commission</strong>, paid monthly!
            </p>

            {/* Features Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white/50 dark:bg-gray-700/50 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 text-center hover:-translate-y-2 border border-gray-200/50 dark:border-gray-600/50 hover:border-primary-200 dark:hover:border-primary-700">
                <div className="bg-primary-50 dark:bg-primary-900/30 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6 border border-primary-100 dark:border-primary-800">
                  <Percent size={32} className="text-primary-600 dark:text-primary-400" />
                </div>
                <h3 className="font-semibold text-lg mb-3 text-gray-900 dark:text-gray-100">40% Recurring Commission</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  Earn up to 40% of every subscription fee for the life of your referrals.
                </p>
              </div>

              <div className="bg-white/50 dark:bg-gray-700/50 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 text-center hover:-translate-y-2 border border-gray-200/50 dark:border-gray-600/50 hover:border-primary-200 dark:hover:border-primary-700">
                <div className="bg-primary-50 dark:bg-primary-900/30 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6 border border-primary-100 dark:border-primary-800">
                  <CalendarIcon size={32} className="text-primary-600 dark:text-primary-400" />
                </div>
                <h3 className="font-semibold text-lg mb-3 text-gray-900 dark:text-gray-100">Paid Monthly or Yearly</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  Receive payments on each renewal—monthly or yearly.
                </p>
              </div>

              <div className="bg-white/50 dark:bg-gray-700/50 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 text-center hover:-translate-y-2 border border-gray-200/50 dark:border-gray-600/50 hover:border-primary-200 dark:hover:border-primary-700">
                <div className="bg-primary-50 dark:bg-primary-900/30 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6 border border-primary-100 dark:border-primary-800">
                  <Share2 size={32} className="text-primary-600 dark:text-primary-400" />
                </div>
                <h3 className="font-semibold text-lg mb-3 text-gray-900 dark:text-gray-100">Easy to Share</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  Get your unique link and code and start earning in seconds.
                </p>
              </div>

              <div className="bg-white/50 dark:bg-gray-700/50 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 text-center hover:-translate-y-2 border border-gray-200/50 dark:border-gray-600/50 hover:border-primary-200 dark:hover:border-primary-700">
                <div className="bg-primary-50 dark:bg-primary-900/30 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6 border border-primary-100 dark:border-primary-800">
                  <Headset size={32} className="text-primary-600 dark:text-primary-400" />
                </div>
                <h3 className="font-semibold text-lg mb-3 text-gray-900 dark:text-gray-100">Dedicated Support</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  Access our partner resources and expert help.
                </p>
              </div>

              <div className="bg-white/50 dark:bg-gray-700/50 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 text-center hover:-translate-y-2 border border-gray-200/50 dark:border-gray-600/50 hover:border-primary-200 dark:hover:border-primary-700">
                <div className="bg-primary-50 dark:bg-primary-900/30 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6 border border-primary-100 dark:border-primary-800">
                  <BarChart2 size={32} className="text-primary-600 dark:text-primary-400" />
                </div>
                <h3 className="font-semibold text-lg mb-3 text-gray-900 dark:text-gray-100">Performance Reports</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  Track clicks, sign-ups, and earnings in real time.
                </p>
              </div>

              <div className="bg-white/50 dark:bg-gray-700/50 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 text-center hover:-translate-y-2 border border-gray-200/50 dark:border-gray-600/50 hover:border-primary-200 dark:hover:border-primary-700">
                <div className="bg-primary-50 dark:bg-primary-900/30 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6 border border-primary-100 dark:border-primary-800">
                  <BookOpen size={32} className="text-primary-600 dark:text-primary-400" />
                </div>
                <h3 className="font-semibold text-lg mb-3 text-gray-900 dark:text-gray-100">Partner Resources</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  Get marketing assets, guides, and best practices.
                </p>
              </div>
            </div>

            {/* Call to Action Buttons - Side by side on desktop, stacked on mobile */}
            <div className="text-center mt-12">
              <div className="flex flex-col md:flex-row gap-4 justify-center items-center">
                <Button
                  as="a"
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="bg-black hover:bg-gray-800 dark:bg-white dark:hover:bg-gray-100 text-white dark:text-black font-medium px-10 py-4 text-lg !rounded-full shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                >
                  Apply & Start Earning Now!
                </Button>
                
                {/* Affiliate Dashboard Button - Only for Affiliates */}
                {!affiliateLoading && isAffiliate && (
                  <Button
                    as={Link}
                    to="/app/affiliate-portal"
                    className="bg-primary-700 dark:bg-primary-600 hover:bg-primary-800 dark:hover:bg-primary-700 text-white font-medium px-8 py-4 text-lg !rounded-full shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                  >
                    <Settings size={20} className="mr-2" />
                    Affiliate Dashboard
                  </Button>
                )}
              </div>
              <p className="mt-4 text-gray-700 dark:text-gray-300">{SUPPORT_EMAIL}</p>
              
            </div>
        </CardContent>
      </Card>
    </div>
  );
}